import { Button, formatBytes } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import { keyframes } from "@emotion/css";
import styled from "@emotion/styled";
import type { FC } from "react";
import React from "react";
import { AnimatePresence, motion, type Variants } from "framer-motion";
import translate from "src/lib/translate";
import mimeTypeLib from "src/lib/mimeType";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import { appViewState, useBloc } from "src/state/state";
import { TranslationKey } from "src/types/translationKey";
import Logo from "src/ui/assets/icons/Logo";
import BlockingLoadingOverlayController, {
  BlockingLoadingOverlayBloc
} from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";
import Translate from "src/ui/components/Translate/Translate";
import SunriseBackground from "src/ui/components/SunriseBackground/SunriseBackground";

import LoadingCompleteIconImage from "./LoadingComplete.svg";
import LoaderIconImage from "./LoadingIconSmall.webp";
import LoaderIconImagePartner from "./LoadingIconSmallPartner.webp";
import { ProgressBar } from "./ProgressBar";

const Fullpage = styled(motion.div)`
  label: BlockingLoadingOverlayFullpage;
  position: fixed;
  top: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.65);
  z-index: 99999999;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  height: 100svh;
  --spinner-scale: 1;
  transition:
    opacity 0.3s ease-in-out,
    background-color 0.3s ease-in-out;
  backdrop-filter: blur(4px);

  &[data-status="loading"],
  &[data-status="error"],
  &[data-status="success"],
  &[data-status="shareReady"] {
    opacity: 1;
    pointer-events: all;

    &[data-fade-in="false"] {
      transition: none;
    }
  }

  &[data-status="error"] {
    background-color: rgba(0, 0, 0, 0.75);
  }

  &[data-bg="solid"] {
    background: #000;
    &::before {
      content: "";
      position: absolute;
      inset: 0;
      opacity: 0.4;
      background: linear-gradient(
        60deg,
        var(--color-sunrise-blue) 13.49%,
        var(--color-sunrise-pink) 51.22%,
        var(--color-sunrise-orange) 85.11%
      );
    }
  }

  &[data-bg="partner"] {
    background: var(--color-cream);
    --spinner-scale: 0.6;

    #branding-logo {
      display: flex;
    }
  }

  &[data-bg="branded"] {
    background: var(--color-cream);
    --logo-pos: calc(var(--safe-area-inset-top, 0) + 1.8rem);

    #branding-logo {
      display: flex;
    }
  }

  &[data-bg="biometric"] {
    background: transparent;
    align-items: flex-start;
    padding-top: calc(var(--safe-area-inset-top, 0px) + 4rem);
  }

  #branding-logo {
    display: none;
  }
`;

const BiometricBackground = styled(SunriseBackground)`
  z-index: 0;
`;

const ErrorContent = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: min(100%, 370px);
  padding: 0 1.6rem;
  z-index: 10;

  .color {
    color: white;
  }

  h3,
  p {
    text-wrap: balance;
  }
`;

const SpinKeyframes = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ContentWrap = styled(motion.div)`
  position: absolute;
  transform: scale(var(--spinner-scale, 1));
  text-align: center;

  h3,
  p {
    font-family: var(--font-family);
    color: var(--Greys-White, #fff);
    letter-spacing: -0.36px;
    padding: 0 1.6rem;
  }

  h3 {
    font-weight: 500;
    margin-top: 0.8rem;
    font-size: 1.25rem;
  }

  p {
    font-weight: 400;
    font-size: 1rem;
    font-family: var(--font-family);
  }

  img {
    display: block;
    width: 4rem;
    height: 4rem;
    margin: 0 auto;
  }

  &.loading-progress img {
    animation: ${SpinKeyframes} 1.2s linear infinite;
  }
`;

const NineLogo = styled(motion.div)`
  position: absolute;
  top: var(--logo-pos, calc(50svh - 4.5rem));
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;

  svg {
    display: block;
    transform: scale(1.2);
  }
`;

const ShareReadyContent = styled(motion.div)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: min(100%, 370px);
  padding: 0 1.6rem;
  z-index: 10;

  .color {
    color: white;
  }

  .file-info {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    padding: 1rem 1.5rem;
    width: 100%;
    margin-bottom: 1.5rem;
  }

  .file-name {
    font-weight: 600;
    word-break: break-word;
    margin-bottom: 0.5rem;
  }

  .file-details {
    font-size: 0.875rem;
    opacity: 0.8;
  }
`;

const overlayVariants: Variants = {
  hidden: {
    opacity: 0,
    backdropFilter: "blur(0px)"
  },
  visible: {
    opacity: 1,
    backdropFilter: "blur(4px)",
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  },
  exit: {
    opacity: 0,
    backdropFilter: "blur(0px)",
    transition: {
      duration: 0.25,
      ease: "easeInOut"
    }
  }
};

const contentVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.9
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};

const successVariants: Variants = {
  hidden: {
    opacity: 0,
    scale: 0.8
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.34, 1.56, 0.64, 1]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};

const errorVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};

const logoVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.3, delay: 0.1 }
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.2 }
  }
};

const shareReadyVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: {
      duration: 0.2,
      ease: "easeIn"
    }
  }
};

export const LoadingSpinner: FC<{ style?: React.CSSProperties }> = (props) => {
  const [
    {
      title,
      message,
      status,
      progress,
      progressIndeterminate,
      progressLabel,
      onCancel
    }
  ] = useBloc(BlockingLoadingOverlayBloc, {
    create: () => BlockingLoadingOverlayController
  });

  const loadingText = title
    ? translate(title as TranslationKey)
    : translate("loading");
  const messageText = message ? translate(message as TranslationKey) : "";

  const screenReaderText = [loadingText, messageText]
    .filter(Boolean)
    .join(". ");

  const [liveRegionContent, setLiveRegionContent] = React.useState("");

  React.useEffect(() => {
    setLiveRegionContent("");

    const timer = setTimeout(() => {
      if (screenReaderText) {
        setLiveRegionContent(screenReaderText);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [screenReaderText]);

  const handleCancelClick = () => {
    BlockingLoadingOverlayController.callCancel();
  };

  const isActive = status === "loading";
  const showProgressBar = progress !== undefined && !progressIndeterminate;
  const showCancelButton = isActive && onCancel;

  return (
    <ContentWrap
      className="loading-progress"
      style={props.style}
      role={isActive ? "status" : undefined}
      tabIndex={isActive ? -1 : undefined}
      aria-hidden={!isActive}
      variants={contentVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <img
        src={
          appViewState.state.partnerStyles
            ? LoaderIconImagePartner
            : LoaderIconImage
        }
        alt=""
        aria-hidden="true"
      />
      {title && (
        <h3 className="as-h4-large color">
          {translate(title as TranslationKey)}
        </h3>
      )}
      {message && (
        <>
          <div style={{ height: "var(--space-xs)" }} />
          <p className="m0 color">{translate(message as TranslationKey)}</p>
        </>
      )}

      {showProgressBar && (
        <ProgressBar
          progress={progress}
          label={progressLabel}
          showPercentage={true}
        />
      )}

      {showCancelButton && (
        <>
          <nine-spacer s="md"></nine-spacer>
          <Button
            onClick={handleCancelClick}
            className="color"
            hideArrow
            center
            theme="charcoal"
          >
            <Translate msg="cancel" />
          </Button>
        </>
      )}

      <div className="only-sr" aria-live="polite" aria-atomic="true">
        {liveRegionContent}
      </div>
    </ContentWrap>
  );
};

const BlockingLoadingOverlay: FC = () => {
  const [{ status, error, bg, fadeIn, title, message, shareReadyFile }] =
    useBloc(BlockingLoadingOverlayBloc, {
      create: () => BlockingLoadingOverlayController
    });
  const [{ partnerSource }] = useBlocNext(AppViewCubit);

  const handleRetry = () => {
    BlockingLoadingOverlayController.callErrorRetry();
  };

  const handleCancel = () => {
    BlockingLoadingOverlayController.callOnCancel();
    BlockingLoadingOverlayController.endLoading();
  };

  const handleShare = () => {
    BlockingLoadingOverlayController.callShare();
  };

  const handleShareCancel = () => {
    BlockingLoadingOverlayController.endLoading();
  };

  const isVisible = status !== "none";
  const isAriaHidden = status === "none" || status === "success";
  const showLogo = bg === "branded" || bg === "partner";

  const getFileTypeLabel = (mimeType?: string, filename?: string): string => {
    if (mimeType) {
      if (mimeType === "application/pdf") return "PDF";
      if (mimeTypeLib.isImage(mimeType)) return translate("file.type.image");
      const parts = mimeType.split("/");
      if (parts[1]) return parts[1].toUpperCase();
    }
    if (filename) {
      const ext = filename.split(".").pop();
      if (ext) return ext.toUpperCase();
    }
    return translate("file.type.file");
  };

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <Fullpage
          key="blocking-overlay"
          aria-hidden={isAriaHidden}
          data-status={status}
          data-bg={bg}
          data-partner={partnerSource}
          variants={fadeIn ? overlayVariants : undefined}
          initial={fadeIn ? "hidden" : "visible"}
          animate="visible"
          exit="exit"
        >
          {bg === "biometric" && <BiometricBackground />}

          <AnimatePresence mode="wait">
            {showLogo && (
              <NineLogo
                key="branding-logo"
                id="branding-logo"
                variants={logoVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <Logo />
              </NineLogo>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {status === "loading" && bg !== "biometric" && (
              <LoadingSpinner key="loading-spinner" />
            )}

            {status === "success" && (
              <ContentWrap
                key="success-content"
                variants={successVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <img
                  src={LoadingCompleteIconImage}
                  alt={translate("loadingCompleted")}
                />
                {title && (
                  <h3 className="as-h4-large color">
                    {translate(title as TranslationKey)}
                  </h3>
                )}
                {message && (
                  <>
                    <div style={{ height: "var(--space-xs)" }} />
                    <p className="m0 color">
                      {translate(message as TranslationKey)}
                    </p>
                  </>
                )}
              </ContentWrap>
            )}

            {status === "error" && error && (
              <ErrorContent
                key="error-content"
                variants={errorVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <div>
                  <h3 className="as-h4-large color">
                    {error.title
                      ? translate(error.title as TranslationKey)
                      : translate("error.generic.short")}
                  </h3>
                  <div style={{ height: "var(--space-xs)" }} />
                  <p className="m0 color">
                    {error.message
                      ? translate(error.message as TranslationKey)
                      : translate("error.generic.short.tryAgain")}
                  </p>
                </div>
                <div style={{ height: "var(--space-md)" }} />
                {error.retry && (
                  <Button theme="sunrise" onPress={handleRetry}>
                    <Translate msg="tryAgain" />
                  </Button>
                )}
                <div style={{ height: "var(--space-xs)" }} />

                <Button
                  onPress={handleCancel}
                  hideArrow
                  theme={error.retry ? "charcoal" : "sunrise"}
                  outline={!!error.retry}
                  style={{ color: error.retry ? "white" : undefined }}
                >
                  <Translate msg={error.cancelText ?? "cancel"} />
                </Button>
              </ErrorContent>
            )}

            {status === "shareReady" && shareReadyFile && (
              <ShareReadyContent
                key="share-ready-content"
                variants={shareReadyVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <h3 className="as-h4-large color">
                  <Translate msg="share.ready" />
                </h3>
                <nine-spacer s="md"></nine-spacer>
                <div className="file-info">
                  <div className="file-name color">
                    {shareReadyFile.filename}
                  </div>
                  <div className="file-details color">
                    {[
                      getFileTypeLabel(
                        shareReadyFile.mimeType,
                        shareReadyFile.filename
                      ),
                      shareReadyFile.size
                        ? formatBytes(shareReadyFile.size)
                        : null
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                <Button onPress={handleShare} theme="sunrise">
                  <Translate msg="share.shareNow" />
                </Button>
                <nine-spacer s="md"></nine-spacer>
                <Button onClick={handleShareCancel} hideArrow theme="charcoal">
                  <Translate msg="cancel" />
                </Button>
              </ShareReadyContent>
            )}
          </AnimatePresence>
        </Fullpage>
      )}
    </AnimatePresence>
  );
};

export default BlockingLoadingOverlay;
